import {
  Box,
  Grid,
  Tooltip,
  Typography,
  useMediaQuery,
  useTheme,
} from "@mui/material";
import { useQueryClient } from "@tanstack/react-query";
import CheckCircleIcon from "@mui/icons-material/CheckCircle";
import CancelIcon from "@mui/icons-material/Cancel";
import HourglassEmptyIcon from "@mui/icons-material/HourglassEmpty";
import ErrorIcon from "@mui/icons-material/Error";
import React, { useEffect, useState } from "react";
import { COLORS } from "../../assets/theme";
import CustomButton from "../../components/common/CustomButton";
import AutoComplete from "../../components/common/AutoComplete";
import CustomSwitch from "../../components/common/CustomSwitch";
import FileUploadField from "../../components/FileUploadField";
import ImportForm from "../../components/ImportForm";
import ToastNotification from "../../components/ToastNotification";
import config from "../../config.json";
import {
  fetchProviders,
  getProviderOptions,
} from "../../services/ProviderService";
import { IField, Option, Params } from "../../types/table/interfaces.type";
import {
  IExport,
  uploadCostPlansExcel,
  useImportedFilesQuery,
} from "../../services/CostPlanService";
import CustomTextField from "../../components/common/CustomTextField";
import CustomLabel from "../../components/common/CustomLabel";
import CustomTable from "../../components/common/table/CustomTable";
import Actions from "../../components/common/Actions";
import {
  ExportAttachmentApi,
  ExportErrorApi,
  IErrors,
} from "../../services/ExportService";
import CustomDialog from "../../components/common/CustomDialog";
import ExportTable from "../../components/ExportTable";
import GetErrors from "../../components/GetErrors";
import { hasJwtPermission } from "../../services/ServicesHelpers";

const ImportCostPlanPage: React.FC = () => {
  const [provider, setProvider] = useState<string>("");
  const [file, setFile] = useState<File | null>(null);
  const [removeAll, setRemoveAll] = useState<boolean>(false);
  const [providersOptions, setProvidersOptions] = useState<Option[]>([]);
  const [isUploading, setIsUploading] = useState<boolean>(false);
  const [textFields, setTextFields] = useState({
    // rateCount: "1",
    mccmnc: "",
    mcc: "",
    mnc: "",
    rate: "Rate",
    effectiveDate: "",
    operatorName: "",
    countryName: "",
    destinationHeader: "",
  });

  const [appliedFilters] = useState<Params<IExport>>({
    usedBy: "ROUTING_COST",
  });

  const queryClient = useQueryClient();

  const [viewDialogOpen, setViewDialogOpen] = useState<boolean>(false);
  const [selectedExport, setSelectedExport] = useState<IExport | null>(null);
  const [confirmNoDateOpen, setConfirmNoDateOpen] = useState<boolean>(false);

  const actions = Actions({
    onExportAttem: {
      handler: (row: any) => {
        ExportAttachmentApi(row.attachmentId, row.usedBy);
      },
      isDisabled: !hasJwtPermission("DOWNLOAD_COST_PLAN_FILE"),
    },

    onView: {
      handler: (row: any) => {
        setSelectedExport(row as IExport);
        setViewDialogOpen(true);
      },
      isDisabled: !hasJwtPermission("GET_ALL_COST_PLAN_IMPORT_ERRORS"),
    },
  });
  const fields: IField<IExport>[] = [
    {
      key: "vendorConnectionProfileId",
      header: "Provider",
      options: (t) => getProviderOptions(t.vendorConnectionProfileId),
    },
    { key: "fileName", header: "File Name" },
    { key: "createdBy", header: "Created By" },
    { key: "createdDate", header: "Created Date" },
    {
      key: "status",
      header: "Status",
      transform: (row: IExport) => {
        switch (row.status?.toLowerCase()) {
          case "success":
            return (
              <Tooltip title="Full Import succeeded">
                <CheckCircleIcon sx={{ color: "green" }} />
              </Tooltip>
            );
          case "fail":
          case "failed":
            return (
              <Tooltip title="All entries failed">
                <CancelIcon sx={{ color: "red" }} />
              </Tooltip>
            );
          case "pending":
            return (
              <Tooltip title="Pending">
                <HourglassEmptyIcon sx={{ color: "orange" }} />
              </Tooltip>
            );
          case "errors":
            return (
              <Tooltip title="Errors occurred">
                <ErrorIcon sx={{ color: "darkorange" }} />
              </Tooltip>
            );
          default:
            return row.status;
        }
      },
    },
  ];

  const ErrorField: IField<IErrors>[] = [
    { key: "rowNumber", header: "Row Number" },
    { key: "errorMessage", header: "Error Message" },
    { key: "createdBy", header: "Created By" },
    { key: "createdDate", header: "Created Date" },
  ];

  const theme = useTheme();
  const isSmallScreen = useMediaQuery(theme.breakpoints.down("sm"));

  useEffect(() => {
    const loadProviders = async () => {
      try {
        const providerResponse = await fetchProviders({
          size: config.MAX_PAGE_SIZE,
        });
        const providersAsOptions = providerResponse.content.map((provider) => ({
          label: provider.name,
          value: provider.id,
        }));
        setProvidersOptions(providersAsOptions);
      } catch (error: any) {
        ToastNotification.error("Error loading providers. ", error);
        console.error("Error loading providers. ", error);
      }
    };

    loadProviders();
  }, []);

  const handleFileChange = (file: File | null) => {
    setFile(file);
  };

  const performUpload = async () => {
    try {
      setIsUploading(true);
      queryClient.invalidateQueries({ queryKey: ["importedFiles"] });
      queryClient.refetchQueries({ queryKey: ["importedFiles"] });
      await uploadCostPlansExcel(provider, file!, removeAll, textFields);
      ToastNotification.success("File uploaded successfully!");
    } catch (err: any) {
      ToastNotification.error(err);
    } finally {
      setIsUploading(false);
    }
  };

  const handleUpload = async () => {
    if (!provider) {
      ToastNotification.error("Please select a provider.");
      return;
    }

    if (!file) {
      ToastNotification.error("Please select an Excel File file.");
      return;
    }

    if (!textFields.effectiveDate.trim()) {
      setConfirmNoDateOpen(true);
      return;
    }

    await performUpload();
  };

  const formStyles = {
    padding: { xs: 2, sm: 5 },
    borderRadius: "12px",
    backgroundColor: "#fff",
  };

  const uploadButton = (
    <CustomButton
      size="small"
      variant="contained"
      label={isUploading ? "Uploading..." : "Upload"}
      textColor={COLORS.textPrimary}
      bgColor={COLORS.GOLD}
      disabled={isUploading}
      onClick={handleUpload}
      sx={{ mt: 1, ml: "auto" }}
      loading={false}
    />
  );

  const additionalComponents = [
    <Box
      key="row-1"
      display="flex"
      alignItems={{ xs: "stretch", sm: "end" }}
      flexDirection={{ xs: "column", sm: "row" }}
      gap={{ xs: 2.5, sm: 2 }}
      sx={{ 
        marginTop: 2,
        px: { xs: 1, sm: 0 } // Add horizontal padding on mobile
      }}
    >
      <Box 
        flex={{ xs: "1", sm: "48%" }}
        sx={{ 
          width: "100%",
          minWidth: 0 // Prevent overflow
        }}
      >
        <AutoComplete
          label="Provider"
          value={provider}
          highlightLabelStar={true}
          sx={{ "& .MuiInputBase-root": { backgroundColor: COLORS.WhiteBackground } }}
          options={providersOptions}
          placeholder="Select provider"
          onChange={(value) => setProvider(value as string)}
        />
      </Box>

      <Box 
        flex={{ xs: "1", sm: "48%" }}
        sx={{ 
          width: "100%",
          minWidth: 0, // Prevent overflow
          mt: { xs: 1, sm: 0 } // Add top margin on mobile for better spacing
        }}
      >
        <FileUploadField
          title="File"
          placeholder="Add Excel file"
          accept=".xlsx,.xls,.csv"
          popupImageUrl="/RoutingCost.png"
          popupImageAlt="Cost Plan Excel File"
          onFileChange={handleFileChange}
        />
      </Box>
    </Box>,
    <Box sx={{ mt: 1 }}>
      <Typography variant="subtitle1" sx={{ fontWeight: "bold" }}>
        Excel Header Fields
      </Typography>
    </Box>,
    <Grid
      container
      spacing={2}
      sx={{
        overflowX: "none",
        width: "100%",
      }}
      key="text-fields-row"
    >
      {!Boolean(textFields.mcc.trim()) && !Boolean(textFields.mnc.trim()) && (
        <Grid component={"div" as React.ElementType}>
          <CustomTextField
            label="MCCMNC Header"
            placeholder="MCCMNC"
            size="small"
            highlightLabelStar
            bgColor={COLORS.WhiteBackground}
            value={textFields.mccmnc}
            onChange={(e) =>
              setTextFields({ ...textFields, mccmnc: e.target.value })
            }
          />
        </Grid>
      )}

      {!Boolean(textFields.mccmnc.trim()) && (
        <Grid component={"div" as React.ElementType}>
          <CustomTextField
            label="MCC Header"
            placeholder="MCC"
            size="small"
            highlightLabelStar
            bgColor={COLORS.WhiteBackground}
            value={textFields.mcc}
            onChange={(e) =>
              setTextFields({ ...textFields, mcc: e.target.value })
            }
          />
        </Grid>
      )}

      {!Boolean(textFields.mccmnc.trim()) && (
        <Grid component={"div" as React.ElementType}>
          <CustomTextField
            label="MNC Header"
            placeholder="MNC"
            size="small"
            highlightLabelStar
            bgColor={COLORS.WhiteBackground}
            value={textFields.mnc}
            onChange={(e) =>
              setTextFields({ ...textFields, mnc: e.target.value })
            }
          />
        </Grid>
      )}

      <Grid component={"div" as React.ElementType}>
        <CustomTextField
          label="Rate Header"
          placeholder="Rate"
          size="small"
          highlightLabelStar
          bgColor={COLORS.WhiteBackground}
          value={textFields.rate}
          onChange={(e) =>
            setTextFields({ ...textFields, rate: e.target.value })
          }
        />
      </Grid>

      <Grid component={"div" as React.ElementType}>
        <CustomTextField
          label="Effective Date Header"
          placeholder="Effective Date"
          size="small"
          bgColor={COLORS.WhiteBackground}
          value={textFields.effectiveDate}
          onChange={(e) =>
            setTextFields({ ...textFields, effectiveDate: e.target.value })
          }
        />
      </Grid>
      {!Boolean(textFields.operatorName.trim()) && !Boolean(textFields.countryName.trim()) && (
        <Grid component={"div" as React.ElementType}>
          <CustomTextField
            label="Destination Header"
            placeholder="Destination"
            size="small"
            bgColor={COLORS.WhiteBackground}
            value={textFields.destinationHeader}
            onChange={(e) =>
              setTextFields({ ...textFields, destinationHeader: e.target.value })
            }
          />
        </Grid>
      )}

      {!Boolean(textFields.destinationHeader.trim()) && (
        <Grid component={"div" as React.ElementType}>
          <CustomTextField
            label="Operator Name Header"
            placeholder="OperatorName"
            size="small"
            bgColor={COLORS.WhiteBackground}
            value={textFields.operatorName}
            onChange={(e) =>
              setTextFields({ ...textFields, operatorName: e.target.value })
            }
          />
        </Grid>
      )}
      {!Boolean(textFields.destinationHeader.trim()) && (
        <Grid component={"div" as React.ElementType}>
          <CustomTextField
            label="Country Header"
            placeholder="CountryName"
            size="small"
            bgColor={COLORS.WhiteBackground}
            value={textFields.countryName}
            onChange={(e) =>
              setTextFields({ ...textFields, countryName: e.target.value })
            }
          />
        </Grid>
      )}
    </Grid>,
    <Box
      key="row-2"
      display="flex"
      flexDirection={{ xs: "column", sm: "row" }}
      alignItems="center"
      gap={2}
      sx={{ marginTop: 2 }}
    >
      <Box flex={{ xs: "50%", sm: "48%" }}>
        <CustomSwitch
          label="Delete all rates not found in this sheet"
          checked={removeAll}
          onChange={(event) => setRemoveAll(event.target.checked)}
          activeText="ON"
          inactiveText="OFF"
          fontWeight="300"
          sx={{ mt: 3 }}
          fontSize="0.875rem"
        />
      </Box>
      <Box 
        flex={{ xs: "100%", sm: "48%" }}
        sx={{
          display: "flex",
          justifyContent: { xs: "center", sm: "flex-end" },
          mt: { xs: 2, sm: 1 }
        }}
      >
        <CustomButton
          size="medium"
          variant="contained"
          label={"Upload"}
          textColor={COLORS.textPrimary}
          bgColor={COLORS.GOLD}
          onClick={handleUpload}
          sx={{ 
            mt: 1, 
            ml: { xs: 0, sm: "auto" },
            width: { xs: "100%", sm: "auto" },
            minHeight: { xs: "48px", sm: "36px" },
            fontSize: { xs: "1rem", sm: "0.875rem" },
            fontWeight: { xs: "500", sm: "400" },
            padding: { xs: "12px 24px", sm: "8px 16px" }
          }}
          loading={false}
          permission="IMPORT_ROUTING_COST"
        />
      </Box>
    </Box>,
  ];


  return (
    <Box
      sx={{
        px: { xs: 2, sm: 3 },
        py: { xs: 1.5, sm: 3 },
        width: "100%",
        boxSizing: "border-box",
      }}
    >
      <CustomLabel
        text="Import Cost Plan"
        variant={isSmallScreen ? "h6" : "h5"}
        fontWeight={500}
        marginBottom={1}
        sx={{ fontSize: { xs: "1.1rem", sm: "1.5rem" } }}
      />

      <ImportForm
        additionalComponents={additionalComponents}
        gap={3}
        layout="column"
        sx={formStyles}
      />
      <Box mt={{ xs: 2, sm: 3 }}>
        <CustomLabel
          text="Cost Sessions List"
          variant={isSmallScreen ? "h6" : "h5"}
          fontWeight={500}
          sx={{ fontSize: { xs: "1.1rem", sm: "1.5rem" } }}
        />
      </Box>
      <Box
        mt={1}
        sx={{ width: "100%", overflowX: { xs: "auto", sm: "unset" } }}
      >
        <CustomTable<IExport>
          filters={appliedFilters}
          fields={fields}
          fetchFunc={useImportedFilesQuery}
          actions={actions}
          sortableFields={["status", "createdDate", "createdBy"]}
        />
      </Box>

      <CustomDialog
        open={viewDialogOpen}
        onClose={() => setViewDialogOpen(false)}
        title="View Error Details"
        mode="history"
        fields={[
          {
            type: "custom",
            component: (
              <GetErrors
                usedBy="COST_PLAN"
                fields={ErrorField}
                exportId={String(selectedExport)}
              />
            ),
          },
        ]}
        buttons={[{ label: "Close", onClick: () => setViewDialogOpen(false) }]}
      />

      <CustomDialog
        open={confirmNoDateOpen}
        onClose={() => setConfirmNoDateOpen(false)}
        title="Missing Effective Date Header"
        mode="confirm"
        fields={[
          {
            type: "custom",
            component: (
              <Typography variant="body2">
                The <strong>Effective Date Header</strong> field is empty. Proceeding will import
                the file without mapping an effective date column. Are you sure you want to
                continue?
              </Typography>
            ),
          },
        ]}
        buttons={[
          {
            label: "Proceed",
            onClick: async () => {
              setConfirmNoDateOpen(false);
              await performUpload();
            },
          },
        ]}
      />
    </Box>
  );
};

export default ImportCostPlanPage;
